import type { CommunicationServiceManagementClient } from "@azure/arm-communication";
import type { EmailFeatureStamp, InstalledStamp } from "@m8t-stack/platform-release";
import { provisionAcsEmail } from "./acs-provision.js";
import { LocalCliError } from "./errors.js";

/** Tag key stamped on an ACS this platform provisioned for its own outbound email, so a
 *  later discovery pass can tell it apart from any other sending ACS in the same RG. */
export const ACS_PLATFORM_TAG = "m8t:email";
const ACS_PLATFORM_TAG_VALUE = "platform";
const VERIFIED = "Verified";

export interface AcsIdentity {
  /** Data-plane endpoint, e.g. `https://acs-x.unitedstates.communication.azure.com/`. */
  endpoint: string;
  /** Managed-domain sender, e.g. `DoNotReply@<guid>.azurecomm.net`. */
  sender: string;
}

/** Where the identity came from. Callers surface this so an operator can see whether a
 *  run adopted an existing resource or created one. */
export type AcsSource = "stamp" | "discovered" | "provisioned";

export interface AcsResolution {
  source: AcsSource;
  acs: AcsIdentity;
  /** The ACS resource name, for progress output. Absent on the `stamp` path, which
   *  deliberately never looks the resource up. */
  acsName?: string;
}

export interface ResolveAcsArgs {
  client: CommunicationServiceManagementClient;
  /** The install's platform stamp, or null when it is missing or unreadable. */
  stamp: InstalledStamp | null;
  subscriptionId: string;
  resourceGroup: string;
  /** The rename-stable install suffix shared by every other platform resource
   *  (`m8t-kv-<suffix>`, `m8t-gateway-<suffix>`, …).
   *
   *  May be null: it is needed ONLY by the provisioning leg, so a caller that cannot work it
   *  out can still resolve from the stamp or by discovery. Reaching provisioning without one
   *  is a clear error rather than a fabricated resource name. */
  suffix: string | null;
  dataLocation: string;
  onProgress?: (m: string) => void;
}

/** Build the stamp fragment for a resolved identity.
 *
 *  Identity is recorded even when `enabled` is false, so turning email back on later is a
 *  flag flip rather than another discovery pass. */
export function emailFeatureFrom(acs: AcsIdentity, enabled: boolean): EmailFeatureStamp {
  return { enabled, acsEndpoint: acs.endpoint, acsSender: acs.sender };
}

/** The install's rename-stable suffix, read out of its Key Vault URI
 *  (`https://m8t-kv-<suffix>.vault.azure.net/` → `<suffix>`).
 *
 *  Every platform resource shares this token, and — unlike an agent name — it survives a
 *  rename, which is the failure this whole path exists to prevent. Falls back to the vault
 *  name for a hand-built install whose vault does not follow the convention, and returns
 *  null when nothing usable can be read (callers then require an explicit value). */
export function platformSuffixFromKvUri(kvUri: string): string | null {
  const host = /^https?:\/\/([^./]+)\./i.exec(kvUri.trim())?.[1];
  if (!host) return null;
  return /^m8t-kv-(.+)$/i.exec(host)?.[1] ?? host;
}

/** Pull the resource group out of an ARM id, or null if it carries none (a
 *  subscription-scoped id, for instance). */
export function resourceGroupFromArmId(armId: string): string | null {
  return /\/resourceGroups\/([^/]+)/i.exec(armId)?.[1] ?? null;
}

/** Where an ACS resource may legally be placed, given the executor's Contributor scope. */
export type GrantScopeClass =
  /** Grant is exactly one resource group: the ACS must go there. */
  | { kind: "resource-group"; subscriptionId: string; resourceGroup: string }
  /** Grant is the whole subscription: any resource group in it is authorised. */
  | { kind: "subscription"; subscriptionId: string }
  /** Nothing safe can be derived. `reason` is operator-facing. */
  | { kind: "unusable"; reason: string };

/**
 * Classify the executor's Contributor scope for the purpose of placing an ACS resource.
 *
 * 🔴 The ACS resource must live somewhere the executor's own Contributor grant reaches, or
 * the deployment ends up believing it can send and then 403-ing at the wire. That invariant
 * used to be enforced crudely, by demanding `--resource-group`. Deriving instead is fine —
 * but ONLY from the grant scope itself. Deriving from anything else (an explicit
 * `--resource-group` that differs from the scope, or the Foundry account's resource group)
 * silently breaks the invariant in three ways this function refuses:
 *
 *   - a scope DEEPER than a resource group grants Contributor on one resource only, so its
 *     enclosing resource group is not authorised;
 *   - a scope in a DIFFERENT subscription than the one being deployed into authorises
 *     nothing here;
 *   - a malformed scope tells us nothing at all.
 *
 * `expectedSubscriptionId` is the subscription the ACS client will target.
 */
export function classifyGrantScope(grantScope: string, expectedSubscriptionId: string): GrantScopeClass {
  const unusable = (reason: string): GrantScopeClass => ({ kind: "unusable", reason });
  // Parsed by segment rather than by regex with optional groups: an optional group is
  // undefined at runtime but typed `string` on RegExpExecArray, so the presence checks would
  // look redundant to the linter — and removing them would remove the guards that keep an
  // unauthorised scope from being accepted.
  const parts = grantScope.trim().replace(/^\/+/, "").replace(/\/+$/, "").split("/");

  if (parts.length < 2 || parts[0]?.toLowerCase() !== "subscriptions") {
    return unusable(`'${grantScope}' is not a recognisable Azure scope`);
  }
  const subscriptionId = parts[1] ?? "";
  if (subscriptionId === "") return unusable(`'${grantScope}' names no subscription`);
  if (subscriptionId.toLowerCase() !== expectedSubscriptionId.toLowerCase()) {
    return unusable(
      `the Contributor scope is in subscription ${subscriptionId}, but this deploy targets ` +
        `${expectedSubscriptionId} — an ACS created here would not be covered by that grant`,
    );
  }

  // /subscriptions/<id>
  if (parts.length === 2) return { kind: "subscription", subscriptionId };

  if (parts.length < 4 || parts[2]?.toLowerCase() !== "resourcegroups") {
    return unusable(`'${grantScope}' is neither a subscription nor a resource-group scope`);
  }
  const resourceGroup = parts[3] ?? "";
  if (resourceGroup === "") return unusable(`'${grantScope}' names no resource group`);

  // Anything past /resourceGroups/<rg> is a single resource.
  if (parts.length > 4) {
    return unusable(
      `the Contributor scope is a single resource (${grantScope}), so no resource group is ` +
        "authorised for an ACS data-plane send",
    );
  }
  return { kind: "resource-group", subscriptionId, resourceGroup };
}

interface ParsedDomainId {
  emailServiceName: string;
  domainName: string;
}

/** Pull the email-service and domain names out of a linked-domain ARM id. Returns null on
 *  anything that does not match, so a malformed id skips a candidate instead of throwing. */
function parseDomainId(id: string): ParsedDomainId | null {
  const m = /\/emailServices\/([^/]+)\/domains\/([^/]+)/i.exec(id);
  if (!m?.[1] || !m[2]) return null;
  return { emailServiceName: m[1], domainName: m[2] };
}

interface Candidate {
  name: string;
  hostName: string;
  linkedDomains: string[];
  tagged: boolean;
}

/**
 * Resolve the ACS identity that serves this install's outbound email, in three steps:
 *
 *   1. **Stamp** — if the stamp records an endpoint AND a sender, use them and make no
 *      Azure calls at all.
 *   2. **Discover** — adopt an ACS in the resource group whose linked managed domain is
 *      `Verified`.
 *   3. **Provision** — create `m8t-acs-<suffix>` / `m8t-email-<suffix>` and tag it.
 *
 * Why the stamp comes first: ACS names used to be derived as `acs-${agentName}`, so
 * renaming the executor from `azure-executor` to `ezra-executor` orphaned a perfectly good
 * ACS and silently dropped outbound email. Recording the resolved values takes name
 * derivation off the runtime path entirely — the name is now a one-time provisioning
 * detail rather than something rediscovered on every deploy.
 *
 * This function never writes the stamp. Callers persist `emailFeatureFrom(...)` alongside
 * the `enabled` flag they are setting, so intent and identity are written together.
 */
export async function resolveAcs(args: ResolveAcsArgs): Promise<AcsResolution> {
  const { client, stamp, subscriptionId, resourceGroup, suffix, dataLocation, onProgress } = args;

  // 1. Stamp — authoritative. Note this is keyed on the recorded IDENTITY, not on
  // `enabled`: a disabled install still knows which ACS is its own, so re-enabling is a
  // flag flip rather than a rediscovery.
  const recorded = stamp?.features?.email;
  if (recorded?.acsEndpoint && recorded.acsSender) {
    onProgress?.("using the ACS sender recorded in the platform stamp.");
    return { source: "stamp", acs: { endpoint: recorded.acsEndpoint, sender: recorded.acsSender } };
  }

  // 2. Discover. Collect first, then sort, so the probe order — and therefore the adopted
  // resource — is identical run to run when several candidates are viable.
  onProgress?.(`looking for an existing email-capable ACS in ${resourceGroup}…`);
  const candidates: Candidate[] = [];
  for await (const r of client.communicationServices.listByResourceGroup(resourceGroup)) {
    const linked = r.linkedDomains;
    // No linked domain means it cannot send. The gateway's cost-report ACS sits in this
    // same RG in exactly that state, and adopting it as the sender would be wrong.
    if (!linked || linked.length === 0) continue;
    // The host carries a region infix that cannot be constructed from the name, so a
    // resource that does not report one is skipped rather than guessed at.
    if (!r.name || !r.hostName) continue;
    candidates.push({
      name: r.name,
      hostName: r.hostName,
      linkedDomains: linked,
      tagged: r.tags?.[ACS_PLATFORM_TAG] === ACS_PLATFORM_TAG_VALUE,
    });
  }
  candidates.sort((a, b) => (a.tagged === b.tagged ? a.name.localeCompare(b.name) : a.tagged ? -1 : 1));

  for (const c of candidates) {
    const sender = await verifiedSenderFor(client, resourceGroup, c);
    if (sender === null) continue;
    onProgress?.(`adopting the existing ACS ${c.name} (verified managed domain).`);
    return { source: "discovered", acsName: c.name, acs: { endpoint: `https://${c.hostName}/`, sender } };
  }

  // 3. Provision, keyed off the install suffix so no future rename can orphan it.
  if (suffix === null || suffix === "") {
    throw new LocalCliError({
      code: "NO_PLATFORM_SUFFIX",
      message:
        "No email-capable ACS exists here and one cannot be named: this install's resource suffix could not be worked out.",
      hint: "Pass --kv-uri <install vault uri> (or set AZURE_KEYVAULT_URI) so the ACS can be named consistently with the rest of the platform.",
    });
  }
  const acsName = `m8t-acs-${suffix}`;
  const emailName = `m8t-email-${suffix}`;
  onProgress?.(`no email-capable ACS found — provisioning ${acsName}…`);
  const provisioned = await provisionAcsEmail({
    client,
    subscriptionId,
    resourceGroup,
    name: `m8t-${suffix}`,
    acsName,
    emailName,
    dataLocation,
    tags: { [ACS_PLATFORM_TAG]: ACS_PLATFORM_TAG_VALUE },
    onProgress,
  });
  return { source: "provisioned", acsName, acs: { endpoint: provisioned.endpoint, sender: provisioned.sender } };
}

/** The `DoNotReply@…` sender for a candidate's first Verified linked domain, or null if it
 *  has none. A domain that cannot be read is treated as not viable — one broken link must
 *  not hide a working ACS listed behind it. */
async function verifiedSenderFor(
  client: CommunicationServiceManagementClient,
  resourceGroup: string,
  c: Candidate,
): Promise<string | null> {
  for (const id of c.linkedDomains) {
    const parsed = parseDomainId(id);
    if (!parsed) continue;
    try {
      const d = await client.domains.get(resourceGroup, parsed.emailServiceName, parsed.domainName);
      if (d.verificationStates?.domain?.status !== VERIFIED) continue;
      if (!d.fromSenderDomain) continue;
      return `DoNotReply@${d.fromSenderDomain}`;
    } catch {
      continue;
    }
  }
  return null;
}
